import { ArrowLeft } from 'lucide-react';
import React, { useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { ActionButton } from './ActionButton';

interface ZurueckstellenDialogProps {
  onZurueckstellen: (kommentar: string) => void;
  isBulkOperation?: boolean;
  disabled?: boolean;
  loading?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  selectedCount?: number;
}

export const ZurueckstellenDialog: React.FC<ZurueckstellenDialogProps> = ({
  onZurueckstellen,
  isBulkOperation = false,
  disabled = false,
  loading = false,
  open,
  onOpenChange,
  selectedCount = 1,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [kommentar, setKommentar] = useState('');

  const maxKommentarLength = 2000;
  const isKommentarTooLong = kommentar.length > maxKommentarLength;
  const canConfirm =
    kommentar.trim().length > 0 && !isKommentarTooLong && !loading && !disabled;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setKommentar('');
    }
    if (isBulkOperation) {
      onOpenChange?.(nextOpen);
    } else {
      setIsOpen(nextOpen);
    }
  };

  const handleConfirm = () => {
    if (!canConfirm) {
      return;
    }
    onZurueckstellen(kommentar.trim());
    setKommentar('');
    if (!isBulkOperation) {
      setIsOpen(false);
    }
  };

  const title = isBulkOperation
    ? 'Vorgänge zurückstellen'
    : 'Vorgang zurückstellen';
  const description =
    isBulkOperation && selectedCount > 1
      ? `Möchten Sie wirklich ${selectedCount} Vorgänge zurückstellen?`
      : 'Bitte beschreiben Sie kurz, warum der Vorgang zurückgestellt werden soll. Der Kommentar wird im Protokoll erfasst.';

  const content = (
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>

      <div className="space-y-2">
        <label
          htmlFor="zurueckstellen-kommentar"
          className="text-sm font-semibold text-text-primary"
        >
          Kommentar
        </label>
        <Textarea
          id="zurueckstellen-kommentar"
          value={kommentar}
          onChange={(event) => setKommentar(event.target.value)}
          placeholder="Begründung für die Zurückstellung"
          aria-invalid={isKommentarTooLong}
          disabled={loading || disabled}
        />
      </div>
      <div className="flex items-center justify-between text-xs text-text-secondary">
        <span>Maximal {maxKommentarLength} Zeichen</span>
        <span
          className={
            isKommentarTooLong ? 'text-red-600 font-medium' : undefined
          }
        >
          {kommentar.length}/{maxKommentarLength}
        </span>
      </div>

      <AlertDialogFooter>
        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
        <AlertDialogAction
          onClick={handleConfirm}
          disabled={!canConfirm}
          className="bg-violet-500 hover:bg-violet-600 text-white"
        >
          {loading ? 'Wird zurückgestellt...' : 'Zurückstellen'}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  );

  if (isBulkOperation) {
    return (
      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        {content}
      </AlertDialog>
    );
  }

  return (
    <AlertDialog open={isOpen} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>
        <ActionButton
          variant={'shelve'}
          disabled={disabled || loading}
          loading={loading}
          loadingText="Wird zurückgestellt..."
          normalText="Zurückstellen"
          icon={ArrowLeft}
        />
      </AlertDialogTrigger>
      {content}
    </AlertDialog>
  );
};
